import { type CollectionDistrict, type CollectionIndex } from '../../../../models/peerToPeer/Collection'
import { DistrictSelection } from './DistrictSelection'
import { ReportingOverview } from './ReportingOverview'
import { SubmissionSummary } from './SubmissionSummary'
import { GettingStartedStep } from './steps/GettingStartedStep'
import { ProgramStatusStep } from './steps/ProgramStatusStep'
import { BuildingsWithoutStep } from './steps/BuildingsWithoutStep'
import { BuildingsWithPane } from './steps/BuildingsWithPane'
import { type WizardStage } from './collectionStages'

interface Props {
  stage: WizardStage
  onStageChange: (stage: WizardStage) => void
  index: CollectionIndex
  reportingYearId: number
  onBackToOverview: () => void
  onIndexChanged: () => void
  onSaved: (savedAt: string) => void
  onSubmit: () => void
}

export const CollectionStage = ({
  stage, onStageChange, index, reportingYearId, onBackToOverview, onIndexChanged, onSaved, onSubmit
}: Props): JSX.Element => {
  const districtById = (id: number): CollectionDistrict | undefined =>
    index.districts.find(district => district.districtOrganizationId === id)

  switch (stage.name) {
    case 'gettingStarted':
      return (
        <GettingStartedStep
          districts={index.districts}
          onBack={onBackToOverview}
          onContinue={(districtId, buildingIds) => { onStageChange({ name: 'programStatus', districtId, buildingIds }) }}
        />
      )

    case 'programStatus':
      return (
        <ProgramStatusStep
          district={districtById(stage.districtId)}
          buildingIds={stage.buildingIds}
          reportingYearId={reportingYearId}
          onBack={() => { stage.fromOverview === true ? onBackToOverview() : onStageChange({ name: 'gettingStarted' }) }}
          onContinue={(withProgram, withoutProgram) => {
            onIndexChanged()
            if (withoutProgram.length > 0) {
              onStageChange({
                name: 'buildingsWithout',
                districtId: stage.districtId,
                buildingIds: withoutProgram,
                withProgramIds: withProgram,
                selectedBuildingIds: stage.buildingIds,
                fromOverview: stage.fromOverview
              })
            } else if (withProgram.length > 0) {
              onStageChange({ name: 'buildingsWith', districtId: stage.districtId, buildingIds: withProgram })
            } else {
              onBackToOverview()
            }
          }}
        />
      )

    case 'buildingsWithout':
      return (
        <BuildingsWithoutStep
          district={districtById(stage.districtId)}
          buildingIds={stage.buildingIds}
          reportingYearId={reportingYearId}
          onBack={() => {
            onStageChange({
              name: 'programStatus',
              districtId: stage.districtId,
              buildingIds: stage.selectedBuildingIds,
              fromOverview: stage.fromOverview
            })
          }}
          onContinue={() => {
            if (stage.withProgramIds.length > 0) {
              onStageChange({ name: 'buildingsWith', districtId: stage.districtId, buildingIds: stage.withProgramIds })
            } else {
              onBackToOverview()
            }
          }}
        />
      )

    case 'buildingsWith':
      return (
        <BuildingsWithPane
          district={districtById(stage.districtId)}
          buildingIds={stage.buildingIds}
          focusBuildingId={stage.focusBuildingId}
          reportingYearId={reportingYearId}
          onBackToOverview={onBackToOverview}
          onSaved={onSaved}
        />
      )

    case 'summary':
      return <SubmissionSummary onBack={onBackToOverview} />

    default:
      // A validly-invited user who hasn't selected a district yet is routed to self-service
      // district selection (available districts under the invited ISD), not the empty overview.
      if (index.needsDistrictSelection && index.districts.length === 0) {
        return <DistrictSelection reportingYearId={reportingYearId} onSaved={onBackToOverview} />
      }
      return (
        <ReportingOverview
          index={index}
          onAddDistrict={() => { onStageChange({ name: 'gettingStarted' }) }}
          onOpenDistrict={(districtId, buildingIds, focusBuildingId) => { onStageChange({ name: 'buildingsWith', districtId, buildingIds, focusBuildingId }) }}
          onStartBuilding={(districtId, buildingId) => { onStageChange({ name: 'programStatus', districtId, buildingIds: [buildingId], fromOverview: true }) }}
          onSubmit={onSubmit}
        />
      )
  }
}
